import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { GetStaticProps } from 'next';
import { 
  Zap, 
  ShoppingCart, 
  Smartphone, 
  Tag, 
  TrendingDown, 
  Clock, 
  CheckCircle, 
  Gift,
  Sparkles,
  ArrowRight,
  Apple,
  Play,
  Download,
  Package,
  Shield,
  Truck,
  Star,
  AlertCircle,
  Percent
} from 'lucide-react';
import DownloadApp from '@/components/shared/DownloadApp';
import { getFullUrl } from "@/lib/utils";

interface CountdownTimerProps {
  targetDate: Date;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      if (distance < 0) {
        clearInterval(timer);
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="flex gap-4 justify-center flex-wrap">
      {Object.entries(timeLeft).map(([unit, value]) => (
        <div key={unit} className="bg-white rounded-lg shadow-lg p-4 min-w-[80px] text-center">
          <div className="text-3xl md:text-4xl font-bold text-orange-600">
            {value.toString().padStart(2, '0')}
          </div>
          <div className="text-sm text-gray-600 uppercase mt-1">{unit}</div>
        </div>
      ))}
    </div>
  );
};

export default function BlackFridayDeals2025() {
  const blackFridayDate = new Date('2025-11-28T00:00:00');

  const dealCategories = [
    {
      icon: <Package className="h-8 w-8" />,
      title: 'OEM Parts',
      discount: 'Up to 70% OFF',
      description: 'Original Equipment Manufacturer parts at unbeatable prices',
      color: 'from-blue-500 to-blue-700'
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: 'Pirate Parts',
      discount: 'Up to 80% OFF',
      description: 'High-quality aftermarket parts for maximum savings',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: <Sparkles className="h-8 w-8" />,
      title: 'New Parts',
      discount: 'Up to 60% OFF',
      description: 'Brand new car parts from trusted suppliers',
      color: 'from-green-500 to-green-700'
    },
    {
      icon: <Tag className="h-8 w-8" />,
      title: '2nd Hand Parts',
      discount: 'Up to 85% OFF',
      description: 'Quality used parts from verified scrap yards',
      color: 'from-purple-500 to-purple-700'
    }
  ];

  const topDeals = [
    {
      category: 'Engine Parts',
      items: ['Engine Oil Filters', 'Air Filters', 'Spark Plugs', 'Timing Belts'],
      discount: '50-70%',
      icon: '🔧'
    },
    {
      category: 'Braking System',
      items: ['Brake Pads', 'Brake Discs', 'Brake Calipers', 'Brake Fluid'],
      discount: '40-65%',
      icon: '🛑'
    },
    {
      category: 'Suspension',
      items: ['Shock Absorbers', 'Struts', 'Control Arms', 'Ball Joints'],
      discount: '45-70%',
      icon: '⚙️'
    },
    {
      category: 'Electrical',
      items: ['Batteries', 'Alternators', 'Starters', 'Ignition Coils'],
      discount: '35-60%',
      icon: '⚡'
    },
    {
      category: 'Body Parts',
      items: ['Bumpers', 'Headlights', 'Tail Lights', 'Mirrors'],
      discount: '40-75%',
      icon: '🚗'
    },
    {
      category: 'Service Kits',
      items: ['Minor Service Kits', 'Major Service Kits', 'Filters', 'Oils'],
      discount: '50-80%',
      icon: '🛠️'
    }
  ];

  const trustFactors = [
    {
      icon: <Shield className="h-6 w-6" />,
      title: 'Verified Suppliers',
      description: 'Only certified spare shops and scrap yards'
    },
    {
      icon: <Truck className="h-6 w-6" />,
      title: 'Fast Delivery',
      description: 'Quick shipping across South Africa'
    },
    {
      icon: <Star className="h-6 w-6" />,
      title: 'Quality Guaranteed',
      description: 'All parts inspected and verified'
    },
    {
      icon: <CheckCircle className="h-6 w-6" />,
      title: 'Secure Shopping',
      description: 'Safe and encrypted transactions'
    }
  ];

  return (
    <>
      <Head>
        <title>Black Friday 2025 Car Parts Deals - Save Up to 85% OFF | OEM, Pirate & 2nd Hand Parts | Car Spares EzyFind South Africa</title>
        <meta
          name="description"
          content="🔥 BLACK FRIDAY 2025! Massive savings up to 85% OFF on car parts in South Africa. Shop OEM, Pirate, new & 2nd hand parts from verified suppliers. iPhone, Android & Huawei apps. Free quotes from 1000+ spare shops & scrap yards. Limited time deals on engine parts, brakes, suspension, body parts & more. Download now!"
        />
        <meta
          name="keywords"
          content="black friday car parts 2025, car spares black friday deals, OEM parts sale, pirate parts discount, 2nd hand car parts, scrap yard deals, car parts south africa, black friday automotive, spare parts deals, engine parts sale, brake parts discount, suspension deals, body parts black friday, car service kits sale, car parts online south africa"
        />
        <link rel="canonical" href={getFullUrl("car-parts-black-friday-deals-2025")} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Black Friday 2025 Car Parts - Up to 85% OFF | EzyFind Car Spares" />
        <meta property="og:description" content="🔥 Biggest Black Friday car parts sale in South Africa! Save up to 85% on OEM, Pirate & 2nd hand parts. Download the app now!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getFullUrl("car-parts-black-friday-deals-2025")} />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Black Friday 2025 Car Parts Deals - Save Up to 85% OFF" />
        <meta name="twitter:description" content="🔥 Massive savings on car parts! OEM, Pirate & 2nd hand parts from verified suppliers." />
        
        {/* Additional SEO Meta Tags */}
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="1 days" />
        <meta name="author" content="EzyFind Car Spares" />
        <meta name="geo.region" content="ZA" />
        <meta name="geo.placename" content="South Africa" />
        
        {/* Schema.org Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SpecialAnnouncement",
            "name": "Black Friday 2025 Car Parts Deals",
            "text": "Massive savings up to 85% OFF on car parts during Black Friday 2025",
            "datePosted": "2025-11-01",
            "expires": "2025-12-02",
            "category": "https://schema.org/Product",
            "spatialCoverage": {
              "@type": "Country",
              "name": "South Africa"
            }
          })}
        </script>
      </Head>

      <div className="min-h-screen bg-black">
        {/* Hero Section with Animated Background */}
        <div className="relative overflow-hidden bg-gradient-to-br from-black via-gray-900 to-orange-900">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, orange 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }} />
          </div>

          {/* Floating Discount Tags Animation */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${i * 0.5}s`,
                  animationDuration: `${5 + Math.random() * 3}s`
                }}
              >
                <Percent className="h-8 w-8 text-orange-500 opacity-20" />
              </div>
            ))}
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            {/* Urgency Banner */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-full text-sm font-bold animate-pulse">
                <AlertCircle className="h-5 w-5" />
                <span>LIMITED TIME ONLY - BLACK FRIDAY 2025</span>
                <AlertCircle className="h-5 w-5" />
              </div>
            </div>

            {/* Main Headline */}
            <div className="text-center mb-12">
              <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
                <span className="bg-gradient-to-r from-orange-400 via-red-500 to-orange-600 bg-clip-text text-transparent animate-gradient">
                  BLACK FRIDAY 2025
                </span>
                <br />
                <span className="text-white">CAR PARTS SALE</span>
              </h1>
              
              <div className="flex items-center justify-center gap-4 mb-6 flex-wrap">
                <div className="bg-red-600 text-white px-8 py-4 rounded-2xl transform rotate-2 shadow-2xl">
                  <div className="text-5xl md:text-6xl font-black">UP TO</div>
                  <div className="text-7xl md:text-8xl font-black">85%</div>
                  <div className="text-3xl md:text-4xl font-black">OFF</div>
                </div>
              </div>

              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-8">
                🔥 South Africa's BIGGEST Car Parts Sale! OEM, Pirate, New & 2nd Hand Parts from 1000+ Verified Suppliers
              </p>

              {/* Countdown Timer */}
              <div className="mb-12">
                <div className="text-orange-400 font-bold text-lg mb-4 flex items-center justify-center gap-2">
                  <Clock className="h-6 w-6 animate-spin" />
                  DEALS END IN:
                </div>
                <CountdownTimer targetDate={blackFridayDate} />
              </div>

              {/* Primary CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                <Link 
                  href="https://www.carsparesezyfind.co.za/product"
                  className="group relative bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:scale-105 transition-transform shadow-2xl flex items-center gap-3"
                >
                  <ShoppingCart className="h-6 w-6" />
                  SHOP BLACK FRIDAY DEALS NOW
                  <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform" />
                  <span className="absolute -top-2 -right-2 bg-yellow-400 text-black text-xs font-bold px-3 py-1 rounded-full animate-bounce">
                    LIVE NOW
                  </span>
                </Link>
              </div>

              {/* App Download Quick Links */}
              <div className="flex flex-wrap justify-center gap-4 items-center">
              <span className="text-gray-400 font-semibold">Download App for Exclusive Deals:</span>
              <a 
                href="https://apps.apple.com/us/app/auto-ezyfind/id1671880793"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                <Apple className="h-5 w-5" />
                <span className="font-semibold">iPhone</span>
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.inno.ezyfind.automotive&pli=1"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                <Play className="h-5 w-5" />
                <span className="font-semibold">Android</span>
              </a>
              <a 
                href="https://appgallery.huawei.com/app/C107696919"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors"
              >
                <Smartphone className="h-5 w-5" />
                <span className="font-semibold">Huawei</span>
              </a>
            </div>
            </div>
          </div>
        </div>

        {/* Deal Categories Section */}
        <div className="bg-gradient-to-b from-gray-900 to-black py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                🎯 EPIC DEALS BY CATEGORY
              </h2>
              <p className="text-xl text-gray-400">
                Choose your category and save BIG on quality car parts
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {dealCategories.map((category, index) => (
                <div 
                  key={index}
                  className="group relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl border border-gray-700 overflow-hidden"
                >
                  {/* Animated Background Glow */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-0 group-hover:opacity-20 transition-opacity duration-300`} />
                  
                  <div className="relative z-10">
                    <div className={`inline-flex p-4 bg-gradient-to-br ${category.color} rounded-xl mb-4 text-white`}>
                      {category.icon}
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {category.title}
                    </h3>
                    
                    <div className="text-3xl font-black bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent mb-3">
                      {category.discount}
                    </div>
                    
                    <p className="text-gray-400 mb-6">
                      {category.description}
                    </p>
                    
                    <Link 
                      href="https://www.carsparesezyfind.co.za/product"
                      className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-bold group-hover:gap-4 transition-all"
                    >
                      Shop Now
                      <ArrowRight className="h-5 w-5" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="text-center">
              <Link 
                href="https://www.carsparesezyfind.co.za/product"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-red-600 text-white px-10 py-5 rounded-full font-bold text-xl hover:scale-105 transition-transform shadow-2xl"
              >
                <Gift className="h-6 w-6" />
                VIEW ALL BLACK FRIDAY DEALS
                <Sparkles className="h-6 w-6" />
              </Link>
            </div>
          </div>
        </div>

        {/* Top Deals Section */}
        <div className="bg-black py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                🔥 HOTTEST DEALS IN EVERY CATEGORY
              </h2>
              <p className="text-xl text-gray-400">
                Browse our most popular categories with unbeatable Black Friday discounts
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {topDeals.map((deal, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-orange-900/30 hover:border-orange-500/50 transition-all duration-300 hover:scale-105"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-5xl">{deal.icon}</div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">{deal.category}</h3>
                      <div className="text-orange-400 font-black text-xl">
                        {deal.discount} OFF
                      </div>
                    </div>
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {deal.items.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-300">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link 
                    href="https://www.carsparesezyfind.co.za/product"
                    className="block text-center bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 rounded-lg font-bold hover:shadow-lg transition-all"
                  >
                    Shop {deal.category} Deals
                  </Link>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link 
                href="https://www.carsparesezyfind.co.za/product"
                className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full font-bold text-lg hover:scale-105 transition-transform shadow-xl"
              >
                <TrendingDown className="h-6 w-6" />
                BROWSE ALL DISCOUNTED PARTS
                <ArrowRight className="h-6 w-6" />
              </Link>
            </div>
          </div>
        </div>

        {/* Why Shop With Us Section */}
        <div className="bg-gradient-to-b from-gray-900 to-black py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                ✨ WHY SHOP OUR BLACK FRIDAY SALE?
              </h2>
              <p className="text-xl text-gray-400">
                Your trusted partner for quality car parts at unbeatable prices
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {trustFactors.map((factor, index) => (
                <div 
                  key={index}
                  className="text-center p-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 hover:border-orange-500/50 transition-all"
                >
                  <div className="inline-flex p-4 bg-gradient-to-br from-orange-500 to-red-600 rounded-full mb-4 text-white">
                    {factor.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {factor.title}
                  </h3>
                  <p className="text-gray-400">
                    {factor.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Stats Section */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              <div className="text-center">
                <div className="text-5xl font-black bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent mb-2">
                  1000+
                </div>
                <div className="text-gray-400 font-semibold">Verified Suppliers</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-black bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent mb-2">
                  85%
                </div>
                <div className="text-gray-400 font-semibold">Max Discount</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-black bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent mb-2">
                  100K+
                </div>
                <div className="text-gray-400 font-semibold">Parts Available</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-black bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent mb-2">
                  24/7
                </div>
                <div className="text-gray-400 font-semibold">Support Available</div>
              </div>
            </div>
          </div>
        </div>

        {/* App Download Section */}
        <div id="download-app" className="bg-gradient-to-br from-orange-600 via-red-600 to-orange-700 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                📱 GET THE APP FOR EXCLUSIVE DEALS
              </h2>
              <p className="text-xl text-orange-100 max-w-3xl mx-auto">
                Download our mobile app and get instant access to Black Friday deals, exclusive app-only discounts, and real-time price updates!
              </p>
            </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                {/* iPhone App */}
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center border border-white/20">
                  <div className="inline-flex p-4 bg-white rounded-full mb-4">
                    <Apple className="h-12 w-12 text-black" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">iPhone App</h3>
                  <p className="text-orange-100 mb-6">Download from App Store</p>
                  <a 
                    href="https://apps.apple.com/us/app/auto-ezyfind/id1671880793"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-full font-bold hover:scale-105 transition-transform"
                  >
                    <Download className="h-5 w-5" />
                    Download iOS
                  </a>
                </div>

                {/* Android App */}
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center border border-white/20">
                  <div className="inline-flex p-4 bg-white rounded-full mb-4">
                    <Play className="h-12 w-12 text-black" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Android App</h3>
                  <p className="text-orange-100 mb-6">Download from Play Store</p>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.inno.ezyfind.automotive&pli=1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-full font-bold hover:scale-105 transition-transform"
                  >
                    <Download className="h-5 w-5" />
                    Download Android
                  </a>
                </div>

                {/* Huawei App */}
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center border border-white/20">
                  <div className="inline-flex p-4 bg-white rounded-full mb-4">
                    <Smartphone className="h-12 w-12 text-black" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Huawei App</h3>
                  <p className="text-orange-100 mb-6">Download from AppGallery</p>
                  <a 
                    href="https://appgallery.huawei.com/app/C107696919"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-full font-bold hover:scale-105 transition-transform"
                  >
                    <Download className="h-5 w-5" />
                    Download Huawei
                  </a>
                </div>
              </div>

            {/* App Benefits */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">
                🎁 APP-ONLY EXCLUSIVE BENEFITS
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-bold text-white mb-1">Extra 5% App Discount</div>
                    <div className="text-orange-100 text-sm">On top of Black Friday prices</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-bold text-white mb-1">Flash Sale Notifications</div>
                    <div className="text-orange-100 text-sm">Never miss a limited-time deal</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-bold text-white mb-1">Quick Quote Requests</div>
                    <div className="text-orange-100 text-sm">Get quotes from 1000+ suppliers instantly</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA Section */}
        <div className="bg-black py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-br from-gray-900 to-black border-4 border-orange-500 rounded-3xl p-12">
              <div className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-2 rounded-full text-sm font-bold mb-6 animate-pulse">
                <Clock className="h-5 w-5" />
                LIMITED TIME OFFER - ACT NOW!
              </div>
              
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                Don't Miss Out on South Africa's
                <br />
                <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
                  BIGGEST CAR PARTS SALE!
                </span>
              </h2>
              
              <p className="text-xl text-gray-400 mb-8">
                Shop now and save up to 85% on OEM, Pirate, New & 2nd Hand car parts from over 1000 verified suppliers across South Africa
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  href="https://www.carsparesezyfind.co.za/product"
                  className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-10 py-5 rounded-full font-bold text-xl hover:scale-105 transition-transform shadow-2xl flex items-center justify-center gap-3"
                >
                  <ShoppingCart className="h-6 w-6" />
                  START SHOPPING NOW
                  <Sparkles className="h-6 w-6" />
                </Link>
              </div>
              
              <div className="mt-8 flex items-center justify-center gap-4 text-gray-400">
                <Shield className="h-5 w-5" />
                <span className="text-sm">Secure Shopping</span>
                <span>•</span>
                <Truck className="h-5 w-5" />
                <span className="text-sm">Fast Delivery</span>
                <span>•</span>
                <Star className="h-5 w-5" />
                <span className="text-sm">Verified Quality</span>
              </div>
            </div>
          </div>
        </div>

        {/* Download App Component */}
        <DownloadApp />
      </div>

      {/* Custom CSS for Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }
        
        .animate-float {
          animation: float 5s ease-in-out infinite;
        }
        
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
    //revalidate: 3600, // Revalidate every hour
  };
};